/**
 * The sign-in card and the account's "Sign-in methods" page.
 *
 * Every way in the server offers, in the order people reach for them: a
 * provider button (Google, Microsoft, ChatGPT, Apple, GitHub — only those the operator
 * configured), a passkey, then email — with a password, or with a one-time
 * code for anyone who would rather not have one. Creating an account by any
 * route keeps the person's own "I am at least 13" attestation: the password
 * and code forms ask with a checkbox; continuing with a provider or passkey
 * says so in words beside the buttons.
 *
 * Kept out of app.ts so the flows can be read in one place; app.ts supplies
 * the modal, the boot step and the handful of API calls through
 * {@link SignInDeps}.
 */
import {
  fetchSignInMethods,
  linkProvider,
  passkeyLoginOptions,
  passkeyLoginVerify,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  type PublicUser,
  removePasskey,
  renamePasskey,
  type SignInMethods,
  type SignInProvider,
  SignInApiError,
  startEmailCode,
  unlinkProvider,
  verifyEmailCode,
} from "./api.js";
import { el, relTime, toast } from "./dom.js";
import { OAUTH_MARK } from "./oauthMarks.js";
import { assertionJson, creationJson, creationOptions, defaultPasskeyName, passkeyErrorMessage, passkeysSupported, requestOptions } from "./passkeys.js";

export interface SignInDeps {
  readonly providers: ReadonlyArray<{ id: SignInProvider; label: string }>;
  readonly passkeys: boolean;
  readonly emailCode: boolean;
  readonly allowsSignup: boolean;
  showModal(title: string, content: HTMLElement): void;
  /** Make the open modal non-dismissable (sign-in is required). */
  lockOverlay(): void;
  brand(tagline: string): HTMLElement;
  /** Keep deep-linked state (a plan, a team invite) across the redirect. */
  beforeLeave(): void;
  onSignedIn(result: { token: string; user: PublicUser }): Promise<void>;
  signup(email: string, password: string, displayName: string | undefined, ageConfirmed: boolean): Promise<{ token: string; user: PublicUser }>;
  loginWithEmail(email: string, password: string): Promise<{ token: string; user: PublicUser }>;
  requestPasswordReset(email: string): Promise<void>;
}

type View = "login" | "signup" | "code";

const TERMS = (): HTMLElement[] => [
  el("a", { href: "https://tavsin.xyz/terms", target: "_blank", rel: "noopener" }, ["Terms"]),
  el("a", { href: "https://tavsin.xyz/privacy", target: "_blank", rel: "noopener" }, ["Privacy Policy"]),
];

const providerName = (id: string | null): string =>
  ({ google: "Google", apple: "Apple", microsoft: "Microsoft", github: "GitHub", chatgpt: "ChatGPT" } as Record<string, string>)[id ?? ""] ?? "The provider";

/** What to tell someone the server sent back with `?auth_error=<code>&provider=<id>`. */
export function authErrorMessage(code: string, provider: string | null): string {
  const p = providerName(provider);
  switch (code) {
    case "oauth_cancelled": return "Sign-in was cancelled.";
    case "oauth_state":
    case "link_expired": return "That sign-in took too long or was opened in another browser. Please try again.";
    case "unverified_email": return `${p} didn't confirm your email address, so it can't open an account. Sign in another way, then link ${p} under Account → Sign-in methods.`;
    case "no_email": return `${p} didn't share an email address. Sign in another way, then link ${p} under Account → Sign-in methods.`;
    case "identity_in_use": return `That ${p} account is already linked to a different Tavsin account.`;
    case "sso_required": return "Your organization requires single sign-on. Use your company's SSO to sign in.";
    case "oauth_unavailable": return "That sign-in option isn't available right now.";
    default: return "Sign-in didn't complete. Please try again.";
  }
}

const markSpan = (id: string): HTMLElement => el("span", { class: "oauth-mark", html: OAUTH_MARK[id] ?? "" });

/** Sign in with whichever passkey this device offers for this site. */
async function signInWithPasskey(deps: SignInDeps, note: HTMLElement, button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  note.textContent = "Waiting for your passkey…";
  try {
    const options = await passkeyLoginOptions();
    const cred = (await navigator.credentials.get({ publicKey: requestOptions(options) })) as PublicKeyCredential | null;
    if (!cred) throw new Error("No passkey was chosen.");
    note.textContent = "Signing in…";
    await deps.onSignedIn(await passkeyLoginVerify(assertionJson(cred)));
  } catch (e) {
    note.textContent = e instanceof SignInApiError ? e.message : passkeyErrorMessage(e);
    button.disabled = false;
  }
}

/** The sign-in / create-account card. */
export function showSignIn(deps: SignInDeps, initial: "login" | "signup" = "login"): void {
  let view: View = deps.allowsSignup ? initial : "login";
  let codeEmail = "";
  let needsAge = false;
  let email = "";

  const render = (): void => {
    const note = el("p", { class: "drawer-note auth-note", role: "status" }, []);
    const emailInput = el("input", { class: "field", type: "email", placeholder: "you@example.com", autocomplete: view === "code" ? "email" : "username", "aria-label": "Email" }) as HTMLInputElement;
    emailInput.value = email;
    emailInput.oninput = () => { email = emailInput.value; };

    const section: HTMLElement[] = [];
    // ── providers + passkey ──
    const quick: HTMLElement[] = [];
    // Once a code is on its way, the card is about that code alone.
    const choosing = !(view === "code" && codeEmail);
    for (const p of choosing ? deps.providers : []) {
      const btn = el("button", { class: `btn oauth-btn oauth-${p.id}`, type: "button" }, [markSpan(p.id), el("span", {}, [`Continue with ${p.label}`])]);
      btn.onclick = () => {
        deps.beforeLeave();
        window.location.href = `/api/auth/oauth/${p.id}/start`;
      };
      quick.push(btn);
    }
    if (choosing && deps.passkeys && passkeysSupported() && view !== "signup") {
      const pk = el("button", { class: "btn oauth-btn oauth-passkey", type: "button" }, [markSpan("passkey"), el("span", {}, ["Sign in with a passkey"])]) as HTMLButtonElement;
      pk.onclick = () => void signInWithPasskey(deps, note, pk);
      quick.push(pk);
    }
    if (quick.length > 0) {
      section.push(el("div", { class: "oauth-stack" }, quick));
      section.push(el("div", { class: "oauth-or" }, ["or use email"]));
    }

    // ── email ──
    const links: HTMLElement[] = [];
    const link = (label: string, go: () => void): HTMLElement => {
      const a = el("a", { href: "#", class: "auth-link" }, [label]);
      a.onclick = (e) => { e.preventDefault(); go(); };
      return a;
    };

    if (view === "code") {
      if (!codeEmail) {
        const send = el("button", { class: "btn btn-primary", type: "button" }, ["Email me a sign-in code"]) as HTMLButtonElement;
        const go = async (): Promise<void> => {
          send.disabled = true;
          note.textContent = "Sending…";
          try {
            await startEmailCode(emailInput.value);
            codeEmail = emailInput.value.trim().toLowerCase();
            render();
          } catch (e) {
            note.textContent = e instanceof Error ? e.message : String(e);
            send.disabled = false;
          }
        };
        send.onclick = () => void go();
        emailInput.onkeydown = (e) => { if (e.key === "Enter") void go(); };
        section.push(el("label", { class: "field-label" }, ["Email"]), emailInput, el("div", { class: "drawer-actions" }, [send]));
        note.textContent = "We'll email you a 6-digit code. No password needed.";
      } else {
        const code = el("input", { class: "field auth-code", type: "text", inputmode: "numeric", autocomplete: "one-time-code", maxlength: "7", placeholder: "123456", "aria-label": "Sign-in code" }) as HTMLInputElement;
        const consent = needsAge ? (el("input", { type: "checkbox", id: "auth-consent" }) as HTMLInputElement) : null;
        const name = needsAge ? (el("input", { class: "field", type: "text", placeholder: "Your name (optional)", autocomplete: "name" }) as HTMLInputElement) : null;
        const submit = el("button", { class: "btn btn-primary", type: "button" }, [needsAge ? "Create account" : "Sign in"]) as HTMLButtonElement;
        const go = async (): Promise<void> => {
          if (consent && !consent.checked) {
            note.textContent = "Please confirm you are at least 13 and agree to the Terms to create an account.";
            return;
          }
          submit.disabled = true;
          try {
            const r = await verifyEmailCode({ email: codeEmail, code: code.value, ...(consent ? { ageConfirmed: true } : {}), ...(name?.value.trim() ? { displayName: name.value.trim() } : {}) });
            await deps.onSignedIn(r);
          } catch (e) {
            if (e instanceof SignInApiError && e.flags.needsAgeConfirmation) {
              needsAge = true;
              const typed = code.value;
              render();
              const again = document.querySelector<HTMLInputElement>(".auth-code");
              if (again) again.value = typed;
              return;
            }
            note.textContent = e instanceof Error ? e.message : String(e);
            submit.disabled = false;
          }
        };
        submit.onclick = () => void go();
        code.onkeydown = (e) => { if (e.key === "Enter") void go(); };
        section.push(el("p", { class: "drawer-note" }, ["We sent a code to ", el("strong", {}, [codeEmail]), ". It expires in 10 minutes."]));
        section.push(el("label", { class: "field-label" }, ["Code"]), code);
        if (consent && name) {
          section.push(el("p", { class: "drawer-note" }, ["There's no account for this email yet — this creates one."]));
          section.push(el("label", { class: "field-label" }, ["Name"]), name);
          const [terms, privacy] = TERMS();
          section.push(el("label", { class: "auth-consent", for: "auth-consent" }, [consent, el("span", {}, ["I am at least 13 years old and agree to the ", terms!, " and ", privacy!, "."])]));
        }
        section.push(el("div", { class: "drawer-actions" }, [submit]));
        links.push(link("Send a new code", () => { codeEmail = ""; needsAge = false; render(); }));
        setTimeout(() => code.focus(), 0);
      }
      links.push(link("Use a password instead", () => { view = "login"; codeEmail = ""; needsAge = false; render(); }));
    } else {
      const password = el("input", { class: "field", type: "password", placeholder: view === "signup" ? "At least 10 characters" : "Password", autocomplete: view === "signup" ? "new-password" : "current-password", "aria-label": "Password" }) as HTMLInputElement;
      const displayName = view === "signup" ? (el("input", { class: "field", type: "text", placeholder: "Your name (optional)", autocomplete: "name" }) as HTMLInputElement) : null;
      const consent = view === "signup" ? (el("input", { type: "checkbox", id: "auth-consent" }) as HTMLInputElement) : null;
      const submit = el("button", { class: "btn btn-primary", type: "button" }, [view === "signup" ? "Create account" : "Sign in"]) as HTMLButtonElement;
      const go = async (): Promise<void> => {
        if (consent && !consent.checked) {
          note.textContent = "Please confirm you are at least 13 and agree to the Terms to create an account.";
          consent.focus();
          return;
        }
        submit.disabled = true;
        submit.textContent = view === "signup" ? "Creating…" : "Signing in…";
        deps.beforeLeave();
        try {
          const r = view === "signup"
            ? await deps.signup(emailInput.value, password.value, displayName?.value || undefined, consent?.checked === true)
            : await deps.loginWithEmail(emailInput.value, password.value);
          await deps.onSignedIn(r);
        } catch (e) {
          note.textContent = e instanceof Error ? e.message : String(e);
          submit.disabled = false;
          submit.textContent = view === "signup" ? "Create account" : "Sign in";
        }
      };
      submit.onclick = () => void go();
      const enter = (e: KeyboardEvent): void => { if (e.key === "Enter") void go(); };
      emailInput.onkeydown = enter;
      password.onkeydown = enter;
      section.push(el("label", { class: "field-label" }, ["Email"]), emailInput, el("label", { class: "field-label" }, ["Password"]), password);
      if (displayName) {
        displayName.onkeydown = enter;
        section.push(el("label", { class: "field-label" }, ["Name"]), displayName);
      }
      if (consent) {
        const [terms, privacy] = TERMS();
        section.push(el("label", { class: "auth-consent", for: "auth-consent" }, [consent, el("span", {}, ["I am at least 13 years old and agree to the ", terms!, " and ", privacy!, "."])]));
      }
      section.push(el("div", { class: "drawer-actions" }, [submit]));
      if (view === "login") {
        if (deps.emailCode) links.push(link("Email me a code instead", () => { view = "code"; render(); }));
        links.push(link("Forgot password?", () => {
          if (!emailInput.value) {
            note.textContent = "Enter your email above, then choose 'Forgot password' again.";
            return;
          }
          deps.requestPasswordReset(emailInput.value)
            .then(() => { note.textContent = "If that email is registered, we sent a reset link."; })
            .catch((e: unknown) => { note.textContent = e instanceof Error ? e.message : String(e); });
        }));
      }
    }

    const switcher = deps.allowsSignup && view !== "code"
      ? el("p", { class: "drawer-note auth-switch" }, [
          view === "signup" ? "Already have an account? " : "New here? ",
          link(view === "signup" ? "Sign in" : "Create one", () => { view = view === "signup" ? "login" : "signup"; render(); }),
        ])
      : null;
    const [terms, privacy] = TERMS();
    const fineprint = quick.length > 0
      ? el("p", { class: "auth-fineprint" }, ["Continuing with a provider, a passkey or a code confirms you are at least 13 and agree to the ", terms!, " and ", privacy!, "."])
      : null;

    deps.showModal(view === "signup" ? "Create your account" : "Sign in",
      el("div", { class: "drawer auth-card" }, [
        deps.brand(view === "signup" ? "Create your workspace — agents, project memory, and your whole repo." : "Welcome back to your compounding software intelligence."),
        ...section,
        note,
        links.length > 0 ? el("div", { class: "auth-links" }, links) : null,
        switcher,
        fineprint,
      ]),
    );
    deps.lockOverlay();
    if (view !== "code") setTimeout(() => emailInput.focus(), 0);
  };
  render();
}

// ── Account → Sign-in methods ──────────────────────────────────────────────

export interface MethodsDeps {
  readonly user: PublicUser;
  showModal(title: string, content: HTMLElement): void;
  /** Sign out and show the sign-in card — for "sign in again". */
  reauth(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
}

const LABEL: Readonly<Record<SignInProvider, string>> = { google: "Google", apple: "Apple", microsoft: "Microsoft", github: "GitHub", chatgpt: "ChatGPT" };

/** List, add and remove the ways into this account. */
export async function openSignInMethods(deps: MethodsDeps): Promise<void> {
  let methods: SignInMethods;
  try {
    methods = await fetchSignInMethods();
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e), "error");
    return;
  }
  const refresh = (): void => void openSignInMethods(deps);
  const act = async (fn: () => Promise<unknown>, done?: string): Promise<void> => {
    try {
      await fn();
      if (done) toast(done, "success");
      refresh();
    } catch (e) {
      if (e instanceof SignInApiError && e.flags.reauth) {
        toast("For your security, sign in again before changing how you sign in.", "info");
        return;
      }
      toast(e instanceof SignInApiError ? e.message : passkeyErrorMessage(e), "error");
    }
  };

  const rows: HTMLElement[] = [];
  if (!methods.recent) {
    const again = el("button", { class: "btn btn-sm", type: "button" }, ["Sign in again"]);
    again.onclick = () => void deps.reauth();
    rows.push(el("div", { class: "methods-banner" }, [el("span", {}, ["To add or remove a sign-in method, sign in again first. You can still see everything here."]), again]));
  }

  // Providers
  rows.push(el("div", { class: "methods-head" }, ["Accounts"]));
  const linked = new Map(methods.identities.map((i) => [i.provider, i]));
  const providers = [...new Set([...methods.available.map((p) => p.id), ...linked.keys()])];
  if (providers.length === 0) rows.push(el("p", { class: "drawer-note" }, ["No providers are set up on this server."]));
  for (const id of providers) {
    const has = linked.get(id);
    const action = el("button", { class: "btn btn-sm", type: "button" }, [has ? "Remove" : "Link"]) as HTMLButtonElement;
    action.disabled = !methods.recent;
    action.onclick = has
      ? () => void act(() => unlinkProvider(id), `${LABEL[id]} removed.`)
      : () => void act(async () => { window.location.href = (await linkProvider(id)).url; });
    rows.push(el("div", { class: "methods-row" }, [
      markSpan(id),
      el("div", { class: "methods-main" }, [
        el("div", {}, [LABEL[id] ?? id]),
        el("div", { class: "methods-sub" }, [has ? `${has.email ?? "linked"} · used ${relTime(has.lastUsedAt)}` : "Not linked"]),
      ]),
      action,
    ]));
  }

  // Passkeys
  rows.push(el("div", { class: "methods-head" }, ["Passkeys"]));
  if (methods.passkeys.length === 0) rows.push(el("p", { class: "drawer-note" }, ["Sign in with Face ID, Touch ID, Windows Hello or your phone — no password to remember or leak."]));
  for (const k of methods.passkeys) {
    const rename = el("button", { class: "btn btn-sm btn-ghost", type: "button" }, ["Rename"]);
    rename.onclick = () => {
      const name = window.prompt("Name this passkey", k.name);
      if (name && name.trim()) void act(() => renamePasskey(k.id, name.trim()));
    };
    const remove = el("button", { class: "btn btn-sm", type: "button" }, ["Remove"]) as HTMLButtonElement;
    remove.disabled = !methods.recent;
    remove.onclick = () => void act(() => removePasskey(k.id), "Passkey removed.");
    rows.push(el("div", { class: "methods-row" }, [
      markSpan("passkey"),
      el("div", { class: "methods-main" }, [el("div", {}, [k.name]), el("div", { class: "methods-sub" }, [k.lastUsedAt ? `Last used ${relTime(k.lastUsedAt)}` : `Added ${relTime(k.createdAt)}`])]),
      rename,
      remove,
    ]));
  }
  if (passkeysSupported()) {
    const add = el("button", { class: "btn btn-primary", type: "button" }, ["Add a passkey"]) as HTMLButtonElement;
    add.disabled = !methods.recent;
    add.onclick = () => void act(async () => {
      const options = await passkeyRegisterOptions();
      const cred = (await navigator.credentials.create({ publicKey: creationOptions(options) })) as PublicKeyCredential | null;
      if (!cred) throw new Error("No passkey was made.");
      await passkeyRegisterVerify(creationJson(cred), defaultPasskeyName());
    }, "Passkey added. You can sign in with it next time.");
    rows.push(el("div", { class: "drawer-actions" }, [add]));
  } else {
    rows.push(el("p", { class: "drawer-note" }, ["This browser can't make passkeys."]));
  }

  // Password
  rows.push(el("div", { class: "methods-head" }, ["Password"]));
  const pw = el("button", { class: "btn btn-sm", type: "button" }, [methods.password ? "Change" : "Set a password"]);
  pw.onclick = () => void deps.requestPasswordReset(deps.user.email)
    .then(() => toast(`We emailed a link to ${deps.user.email} to ${methods.password ? "change" : "set"} your password.`, "success"))
    .catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
  rows.push(el("div", { class: "methods-row" }, [
    el("div", { class: "methods-main" }, [el("div", {}, [methods.password ? "Password is set" : "No password"]), el("div", { class: "methods-sub" }, [methods.password ? "You can also sign in with your email and password." : "You sign in with the methods above."])]),
    pw,
  ]));

  deps.showModal("Sign-in methods", el("div", { class: "drawer methods" }, rows));
}
